import { formatDate } from "node_modules/@angular/common";
import { APP_LOCALE, ERROR_MESSAGES } from "src/app/constants";
import { adaptAddress, Address, createEmptyAddress, getAddressInTwoLines, getFullAddress, isNullAddress } from "./address.model";

export interface License {
  Id?: number;
  AccountNo: string;
  Address: Address;
  Email: string;
  ContactNo: string;
  Remark: string;
  BusinessId: number;
  BranchId: number;
  ValidSince: Date;
  ValidUntil: Date;
  CreatedBy?: string;
  CreatedOn?: Date;
  InvalidatedBy?: string;
  InvalidatedOn?: Date;
  Status?: number;
}

export function createEmptyLicense(): License {
  return {
    AccountNo: '',
    Address: createEmptyAddress(),
    Email: '',
    ContactNo: '',
    Remark: '',
    BusinessId: -1,
    BranchId: -1,
    ValidSince: undefined,
    ValidUntil: undefined,
  }
}

export type LicenseFields =  Pick<License, 'ValidSince' | 'ValidUntil' | 'AccountNo' | 'Remark'>;

export function createEmptyLicenseFields(): LicenseFields {
  return {
    ValidSince: undefined,
    ValidUntil: undefined,
    AccountNo: '',
    Remark: ''
  }
}

// --- Mapper -------------------------------------------------------------------------------------

export interface LicenseDTO {
  id?: number;
  accountNo: string;
  addressLine0: string;
  addressLine1?: string;
  addressLine2?: string;
  postalZone: string;
  cityName: string;
  state: string;
  email?: string;
  contactNo: string;
  remark?: string;
  businessId?: number;
  businessBranchId: number;
  validSince?: string;
  validUntil?: string;
  createdBy?: string;
  createdOn?: string;
  invalidatedBy?: string;
  invalidatedOn?: string;
  status?: number;
}

export function adaptLicense(item: Partial<LicenseDTO>): License {
  const base: License = {
    Id: item.id ?? 0,
    AccountNo: item.accountNo,
    Address: adaptAddress(item),
    Email: item.email ?? '',
    ContactNo: item.contactNo,
    Remark: item.remark ?? '',
    BusinessId: item.businessId,
    BranchId: item.businessBranchId,
    ValidSince: (item.validSince && new Date(item.validSince)),
    ValidUntil: (item.validUntil && new Date(item.validUntil)),
    CreatedBy: item.createdBy,
    CreatedOn: (item.createdOn && new Date(item.createdOn)),
    InvalidatedBy: item.invalidatedBy,
    InvalidatedOn: (item.invalidatedOn && new Date(item.invalidatedOn))
  }

  return {
    ...base,
    Status: getLicenseStatus(base)
  }
}

export function toApiLicense(license: License): LicenseDTO {
  return {
    // businessId: license.BusinessId,
    businessBranchId: license.BranchId,
    ...(license.ValidSince && { validSince: formatDate(new Date(license.ValidSince), 'yyyy-MM-dd', APP_LOCALE) }),
    ...(license.ValidUntil && { validUntil: formatDate(new Date(license.ValidUntil), 'yyyy-MM-dd', APP_LOCALE) }),
    ...(license.AccountNo && { accountNo: license.AccountNo }),
    ...(license.Email && { email: license.Email }),
    contactNo: license.ContactNo,
    addressLine0: license.Address.Line0,
    ...(license.Address.Line1 && { addressLine1: license.Address.Line1 }),
    ...(license.Address.Line2 && { addressLine2: license.Address.Line2 }),
    postalZone: license.Address.PostalZone,
    cityName: license.Address.City,
    state: license.Address.State,
    ...(license.Remark && { remark: license.Remark }),
  }
}

// --- Transform ----------------------------------------------------------------------------------

function getLicenseStatus(license: License): number {
  if (license.InvalidatedBy !== undefined) return LICENSE_STATUS.INVALID;
  else if (isExpired(license.ValidUntil)) return LICENSE_STATUS.EXPIRED;
  else if (expiringInMonths(license.ValidUntil)) return LICENSE_STATUS.EXPIRING;
  else return LICENSE_STATUS.VALID;
}

function isExpired(date: Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date.getTime() < today.getTime();
}

function expiringInMonths(date: Date) {
    const today = new Date();
    today.setMonth(today.getMonth());
    today.setHours(0, 0, 0, 0);
    const later = new Date();
    later.setMonth(today.getMonth() + EXPIRING_MONTHS);
    later.setHours(0, 0, 0, 0);
    return date.getTime() > today.getTime() && date.getTime() <= later.getTime();
  }

// --- Validator ----------------------------------------------------------------------------------

export function isNullLicense(license: License): string[] {
  let errors: string[] = [];
  if (license.BranchId == null)
    errors.push(ERROR_MESSAGES.EMPTY_FIELD('Business Branch'));
  if (license.ValidSince == null)
    errors.push(ERROR_MESSAGES.EMPTY_FIELD('Valid From'));
  if (license.ValidUntil == null)
    errors.push(ERROR_MESSAGES.EMPTY_FIELD('Valid Until'));
  if (license.ContactNo == null || license.ContactNo.trim().length === 0)
    errors.push(ERROR_MESSAGES.EMPTY_FIELD('Contact No.'));
  errors = [...errors, ...isNullAddress(license.Address)];
  return errors;
}

// --- Constant -----------------------------------------------------------------------------------

const EXPIRING_MONTHS = 3;
export const LICENSE_STATUS = {
    VALID: 0,
    EXPIRING: 1,
    EXPIRED: 2,
    INVALID: 3
} as const

export const LICENSE_ACTION = {
  Add: 0,
  Update: 1,
  Renew: 2
} as const

export type LicenseAction = typeof LICENSE_ACTION[keyof typeof LICENSE_ACTION];